import { call, put, select, takeEvery } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { ApiGateway } from '../../services/apiGateway';
import { comoLinkActions } from './actions';
import { getProgramLinkState } from './selectors';
import { ComoLinkStateResponse, LinkStatus, LinkTarget, MerchantLinkState, ProgramEntry } from './types';

type HandOffPayload = ProgramEntry & { merchantSequenceKey: string };

const STATUSES: LinkStatus[] = ['absent', 'pending', 'failed', 'exhausted', 'completed'];
const TARGETS: LinkTarget[] = ['legacy', 'como'];

const parseMerchant = (item: any): MerchantLinkState => {
  const { merchantSequenceKey, target, status, comoBusinessId } = item || {};
  const validBusinessId = comoBusinessId === null || (Number.isInteger(comoBusinessId) && comoBusinessId > 0);
  if (
    typeof merchantSequenceKey !== 'string' ||
    !merchantSequenceKey ||
    !TARGETS.includes(target) ||
    typeof status !== 'string' ||
    !validBusinessId
  ) {
    throw new Error('Invalid COMO merchant link state');
  }
  // A status added by a newer backend must not fail the whole response: the merchant is sent to support.
  return { merchantSequenceKey, target, status: STATUSES.includes(status) ? status : 'failed', comoBusinessId };
};

export const parseComoLinkState = (response: any, programId: string): MerchantLinkState[] => {
  if (!response || response.programId !== programId || !Array.isArray((response as ComoLinkStateResponse).merchants)) {
    throw new Error('Invalid COMO link state response');
  }
  return response.merchants.map(parseMerchant);
};

// Only a safe code is kept: Axios errors carry the request URL and headers.
export const toHandoffCode = (error: any): string => {
  switch (error?.response?.status) {
    case 429:
      return 'RATE_LIMITED';
    case 403:
      return 'FORBIDDEN';
    case 409:
      return 'NOT_LINKED';
    case 502:
      return 'REMOTE_DENIED';
    default:
      return 'UNAVAILABLE';
  }
};

const DEFAULT_RETRY_MS = 60_000;
const MAX_RETRY_MS = 300_000;

// Only a positive number of seconds is honoured; the limiter window is 60s, so that is the fallback.
const retryAtFor = (error: any): number => {
  const seconds = Number(error?.response?.headers?.['retry-after']);
  const ms = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds * 1000, MAX_RETRY_MS) : DEFAULT_RETRY_MS;
  return Date.now() + ms;
};

const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

const isNavigableUrl = (url: unknown): url is string => {
  if (typeof url !== 'string' || !url) return false;
  try {
    const { protocol, hostname } = new URL(url);
    return protocol === 'https:' || (protocol === 'http:' && LOCAL_HOSTS.includes(hostname));
  } catch (e) {
    return false;
  }
};

const entryKey = ({ programId, entryId }: ProgramEntry): string => `${programId}:${entryId}`;

export const createComoLinkSagas = ({ navigate }: { navigate: (url: string) => void }) =>
  function* comoLinkSagas() {
    // One state request per program entry, even if the same entry is dispatched again.
    const requested = new Set<string>();
    const handingOff = new Set<string>();
    const refreshed = new Set<string>();

    function* isCurrentHandOff({ programId, entryId, merchantSequenceKey }: HandOffPayload) {
      const program = yield select(getProgramLinkState, programId);
      return !!program && program.entryId === entryId && program.navigatingMsk === merchantSequenceKey;
    }

    function* loadMerchants(programId: string) {
      const response = yield call(ApiGateway.getComoLinkState, programId);
      return parseComoLinkState(response, programId);
    }

    // A denial means local state may be stale; refresh it once per entry, keeping the old state on failure.
    function* refreshState({ programId, entryId }: ProgramEntry) {
      try {
        const merchants = yield call(loadMerchants, programId);
        yield put(comoLinkActions.fetchState.success({ programId, entryId, merchants }));
      } catch (e) {
        // Keep the existing state.
      }
    }

    function* loadIntoStore(entry: ProgramEntry) {
      try {
        const merchants = yield call(loadMerchants, entry.programId);
        yield put(comoLinkActions.fetchState.success({ ...entry, merchants }));
      } catch (e) {
        yield put(comoLinkActions.fetchState.failure(entry));
      }
    }

    yield takeEvery(
      getType(comoLinkActions.fetchState.request),
      function* ({ payload }: ReturnType<typeof comoLinkActions.fetchState.request>) {
        const key = entryKey(payload);
        if (requested.has(key)) return;
        requested.add(key);

        yield call(loadIntoStore, payload);
      }
    );

    const retrying = new Set<string>();

    yield takeEvery(
      getType(comoLinkActions.retryState),
      function* ({ payload }: ReturnType<typeof comoLinkActions.retryState>) {
        const key = entryKey(payload);
        if (retrying.has(key)) return;
        retrying.add(key);

        try {
          yield call(loadIntoStore, payload);
        } finally {
          retrying.delete(key);
        }
      }
    );

    yield takeEvery(
      getType(comoLinkActions.requestMagicLink),
      function* ({ payload }: ReturnType<typeof comoLinkActions.requestMagicLink>) {
        const { programId, entryId, merchantSequenceKey } = payload;
        const key = entryKey(payload);
        if (handingOff.has(key) || !(yield call(isCurrentHandOff, payload))) return;
        handingOff.add(key);

        try {
          // The URL stays in this scope: an action or state would be recorded by redux-logger.
          const result: { url?: unknown } | undefined = yield call(ApiGateway.getComoMagicLink, merchantSequenceKey);
          if (!isNavigableUrl(result?.url)) throw new Error('Invalid magic link response');
          if (!(yield call(isCurrentHandOff, payload))) return;
          yield call(navigate, result.url);
        } catch (e) {
          if (!(yield call(isCurrentHandOff, payload))) return;
          const code = toHandoffCode(e);
          yield put(
            comoLinkActions.handoffFailed({
              programId,
              entryId,
              code,
              ...(code === 'RATE_LIMITED' ? { retryAt: retryAtFor(e) } : {})
            })
          );
          if ((code === 'FORBIDDEN' || code === 'NOT_LINKED') && !refreshed.has(key)) {
            refreshed.add(key);
            yield call(refreshState, { programId, entryId });
          }
        } finally {
          handingOff.delete(key);
        }
      }
    );
  };
